import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GetAppIcon from '@mui/icons-material/GetApp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareIcon from '@mui/icons-material/Compare';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs from 'dayjs';
import { apiFetch } from '../utils/apiFetch.js';

const reportTemplates = [
  {
    id: 'revenue_summary',
    name: 'Revenue Summary',
    description: 'Comprehensive revenue analysis with trends and comparisons',
    icon: <TrendingUpIcon />,
    metrics: ['total_revenue', 'average_job_value', 'revenue_growth', 'monthly_breakdown'],
    defaultPeriod: 'last_quarter'
  },
  {
    id: 'performance_metrics',
    name: 'Performance Metrics',
    description: 'Key performance indicators and operational efficiency',
    icon: <BarChartIcon />,
    metrics: ['jobs_completed', 'customer_satisfaction', 'on_time_delivery', 'crew_utilization'],
    defaultPeriod: 'last_month'
  },
  {
    id: 'customer_analysis',
    name: 'Customer Analysis',
    description: 'Customer behavior, retention, and lifetime value analysis',
    icon: <AssessmentIcon />,
    metrics: ['new_customers', 'repeat_customers', 'customer_lifetime_value', 'churn_rate'],
    defaultPeriod: 'last_6_months'
  },
  {
    id: 'comparative_analysis',
    name: 'Comparative Analysis',
    description: 'Year-over-year and period-over-period comparisons',
    icon: <CompareIcon />,
    metrics: ['yoy_growth', 'mom_growth', 'seasonal_trends', 'market_share'],
    defaultPeriod: 'custom'
  }
];

const availableMetrics = [
  { id: 'total_revenue', name: 'Total Revenue', category: 'Financial' },
  { id: 'average_job_value', name: 'Average Job Value', category: 'Financial' },
  { id: 'revenue_growth', name: 'Revenue Growth %', category: 'Financial' },
  { id: 'jobs_completed', name: 'Jobs Completed', category: 'Operations' },
  { id: 'customer_satisfaction', name: 'Customer Satisfaction', category: 'Quality' },
  { id: 'on_time_delivery', name: 'On-Time Delivery %', category: 'Operations' },
  { id: 'new_customers', name: 'New Customers', category: 'Marketing' },
  { id: 'repeat_customers', name: 'Repeat Customers', category: 'Marketing' },
  { id: 'crew_utilization', name: 'Crew Utilization %', category: 'Operations' },
  { id: 'customer_lifetime_value', name: 'Customer Lifetime Value', category: 'Financial' }
];

const CustomReportingTools = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customReport, setCustomReport] = useState({
    name: '',
    dateRange: { start: dayjs().subtract(30, 'day'), end: dayjs() },
    metrics: [],
    filters: {},
    comparison: 'none',
    format: 'pdf'
  });
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setCustomReport({
      ...customReport,
      name: template.name,
      metrics: template.metrics
    });
    setReportDialogOpen(true);
  };

  const handleCustomReport = () => {
    setSelectedTemplate(null);
    setCustomReport({
      name: '',
      dateRange: { start: dayjs().subtract(30, 'day'), end: dayjs() },
      metrics: [],
      filters: {},
      comparison: 'none',
      format: 'pdf'
    });
    setReportDialogOpen(true);
  };

  const handleMetricToggle = (metricId) => {
    setCustomReport(prev => ({
      ...prev,
      metrics: prev.metrics.includes(metricId)
        ? prev.metrics.filter(id => id !== metricId)
        : [...prev.metrics, metricId]
    }));
  };

  const generateReport = async () => {
    setGenerating(true);
    
    try {
      const response = await apiFetch('/api/analytics/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: customReport.name,
          dateRange: {
            start: customReport.dateRange.start?.toISOString(),
            end: customReport.dateRange.end?.toISOString()
          },
          metrics: selectedTemplate ? selectedTemplate.metrics : customReport.metrics,
          format: customReport.format
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || `HTTP ${response.status}`);
      
      setSnackbar({ open: true, message: `${data.report.name} generated successfully. Use CSV exports below to download production data.`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to generate report.', severity: 'error' });
    } finally {
      setGenerating(false);
      setReportDialogOpen(false);
    }
  };

  const downloadExport = async (kind) => {
    try {
      const response = await apiFetch(`/api/analytics/export/${kind}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const url = window.URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${kind}-export-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSnackbar({ open: true, message: `${kind === 'bookings' ? 'Bookings' : 'Revenue'} export downloaded.`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to download report.', severity: 'error' });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" fontWeight={700}>
              Custom Reporting
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            onClick={handleCustomReport}
            startIcon={<AssessmentIcon />}
          >
            Create Custom Report
          </Button>
        </Box>

        <Grid container spacing={4}>
          {/* Report Templates */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Report Templates
            </Typography>
            <Grid container spacing={3}>
              {reportTemplates.map((template) => (
                <Grid item xs={12} sm={6} md={3} key={template.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6
                      }
                    }}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {template.icon}
                        <Typography variant="h6" fontWeight={600} sx={{ ml: 1 }}>
                          {template.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {template.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {template.metrics.slice(0, 3).map(metric => (
                          <Chip 
                            key={metric} 
                            label={metric.replace(/_/g, ' ')} 
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {template.metrics.length > 3 && (
                          <Chip 
                            label={`+${template.metrics.length - 3} more`} 
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>
                    </CardContent>
                    <CardActions>
                      <Button size="small" color="primary">
                        Use Template
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Real CSV Exports */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Production Exports
            </Typography>
            <Paper sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These downloads are generated from live bookings and payment records. Scheduled reports and saved report history will stay hidden until they are backed by real data.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="outlined" startIcon={<GetAppIcon />} onClick={() => downloadExport('bookings')}>Download bookings CSV</Button>
                <Button variant="outlined" startIcon={<GetAppIcon />} onClick={() => downloadExport('revenue')}>Download revenue CSV</Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Report Configuration Dialog */}
        <Dialog 
          open={reportDialogOpen} 
          onClose={() => setReportDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedTemplate ? `Configure ${selectedTemplate.name}` : 'Create Custom Report'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Report Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Report Name"
                  value={customReport.name}
                  onChange={(e) => setCustomReport(prev => ({ ...prev, name: e.target.value }))}
                />
              </Grid>

              {/* Date Range */}
              <Grid item xs={6}>
                <DatePicker
                  label="Start Date"
                  value={customReport.dateRange.start}
                  onChange={(date) => setCustomReport(prev => ({
                    ...prev,
                    dateRange: { ...prev.dateRange, start: date }
                  }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="End Date"
                  value={customReport.dateRange.end}
                  onChange={(date) => setCustomReport(prev => ({
                    ...prev,
                    dateRange: { ...prev.dateRange, end: date }
                  }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              {/* Metrics Selection */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Select Metrics
                </Typography>
                <FormGroup>
                  <Grid container>
                    {availableMetrics.map((metric) => (
                      <Grid item xs={6} key={metric.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={customReport.metrics.includes(metric.id)}
                              onChange={() => handleMetricToggle(metric.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">{metric.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {metric.category}
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Grid>

              {/* Comparison and Format */}
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Comparison</InputLabel>
                  <Select
                    value={customReport.comparison}
                    onChange={(e) => setCustomReport(prev => ({ ...prev, comparison: e.target.value }))}
                    label="Comparison"
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="previous_period">Previous Period</MenuItem>
                    <MenuItem value="year_over_year">Year over Year</MenuItem>
                    <MenuItem value="industry_average">Industry Average</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Format</InputLabel>
                  <Select
                    value={customReport.format}
                    onChange={(e) => setCustomReport(prev => ({ ...prev, format: e.target.value }))}
                    label="Format"
                  >
                    <MenuItem value="pdf">PDF Report</MenuItem>
                    <MenuItem value="excel">Excel Spreadsheet</MenuItem>
                    <MenuItem value="powerpoint">PowerPoint Presentation</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReportDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={generateReport}
              variant="contained"
              disabled={generating || !customReport.name || customReport.metrics.length === 0}
              startIcon={generating ? <CircularProgress size={20} /> : <AssessmentIcon />}
            >
              {generating ? 'Generating...' : 'Generate Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default CustomReportingTools;